import type * as Y from 'yjs';
import { boardObjects, type BoardEditMarker, type BoardObject, type BoardTypes } from './model.ts';

export const BOARD_WIDTH = 1280;
export const BOARD_HEIGHT = 720;

function roundedRect(context: CanvasRenderingContext2D, x: number, y: number, width: number, height: number, radius: number): void {
  context.beginPath();
  context.roundRect(x, y, width, height, radius);
}

function wrappedText(context: CanvasRenderingContext2D, text: string, x: number, y: number, maxWidth: number, lineHeight: number): void {
  let line = '';
  let row = 0;
  for (const paragraph of text.split('\n')) {
    for (const word of paragraph.split(/\s+/)) {
      const next = line ? `${line} ${word}` : word;
      if (line && context.measureText(next).width > maxWidth) {
        context.fillText(line, x, y + row * lineHeight);
        row += 1;
        line = word;
      } else line = next;
    }
    if (line) context.fillText(line, x, y + row * lineHeight);
    row += 1;
    line = '';
  }
}

export function paintBoard(context: CanvasRenderingContext2D, objects: readonly BoardObject[]): void {
  context.save();
  context.setTransform(context.canvas.width / BOARD_WIDTH, 0, 0, context.canvas.height / BOARD_HEIGHT, 0, 0);
  const background = context.createLinearGradient(0, 0, BOARD_WIDTH, BOARD_HEIGHT);
  background.addColorStop(0, '#15201d');
  background.addColorStop(1, '#26342f');
  context.fillStyle = background;
  context.fillRect(0, 0, BOARD_WIDTH, BOARD_HEIGHT);
  context.fillStyle = 'rgba(255,255,255,.025)';
  for (let x = 0; x < BOARD_WIDTH; x += 40) context.fillRect(x, 0, 1, BOARD_HEIGHT);
  for (let y = 0; y < BOARD_HEIGHT; y += 40) context.fillRect(0, y, BOARD_WIDTH, 1);

  for (const object of objects) {
    context.save();
    if (object.kind === 'rect') {
      context.fillStyle = object.color;
      roundedRect(context, object.x, object.y, object.width, object.height, 18);
      context.fill();
    } else if (object.kind === 'note') {
      context.shadowColor = '#0007';
      context.shadowBlur = 24;
      context.shadowOffsetY = 12;
      context.fillStyle = object.color;
      roundedRect(context, object.x, object.y, object.width, object.height, 8);
      context.fill();
      context.shadowColor = 'transparent';
      context.fillStyle = '#231f19';
      context.font = '500 25px system-ui, sans-serif';
      context.textBaseline = 'top';
      wrappedText(context, object.text ?? '', object.x + 22, object.y + 24, object.width - 44, 34);
    } else if (object.kind === 'text') {
      context.fillStyle = object.color;
      context.font = object.height >= 80 ? '600 48px Georgia, serif' : '500 30px system-ui, sans-serif';
      context.textBaseline = 'top';
      wrappedText(context, object.text ?? '', object.x, object.y, object.width, 56);
    } else if (object.kind === 'stroke' && object.points?.length) {
      context.strokeStyle = object.color;
      context.lineWidth = 8;
      context.lineCap = 'round';
      context.lineJoin = 'round';
      context.beginPath();
      context.moveTo(object.points[0]!.x, object.points[0]!.y);
      for (const point of object.points.slice(1)) context.lineTo(point.x, point.y);
      context.stroke();
    }
    context.restore();
  }
  context.restore();
}

export interface BoardViewOptions {
  onPaint?: (markers: readonly BoardEditMarker[]) => void;
}

export class BoardView {
  readonly canvas: HTMLCanvasElement;
  readonly #context: CanvasRenderingContext2D;
  readonly #board: BoardTypes;
  readonly #options: BoardViewOptions;
  readonly #resize: ResizeObserver;
  #paintFrame = 0;

  constructor(canvas: HTMLCanvasElement, board: BoardTypes, options: BoardViewOptions = {}) {
    this.canvas = canvas;
    this.#board = board;
    this.#options = options;
    const context = canvas.getContext('2d');
    if (!context) throw new Error('Board canvas2D is unavailable');
    this.#context = context;
    const observe = (): void => this.schedule();
    board.objects.observe(observe);
    board.order.observe(observe);
    board.editMarkers.observe(observe);
    this.#resize = new ResizeObserver(() => this.schedule());
    this.#resize.observe(canvas);
    this.schedule();
  }

  schedule(): void {
    if (this.#paintFrame) return;
    this.#paintFrame = requestAnimationFrame(() => {
      this.#paintFrame = 0;
      this.paint();
    });
  }

  paint(): void {
    const bounds = this.canvas.getBoundingClientRect();
    const ratio = Math.min(devicePixelRatio || 1, 2);
    const width = Math.max(2, Math.round((bounds.width || BOARD_WIDTH) * ratio));
    const height = Math.max(2, Math.round((bounds.height || BOARD_HEIGHT) * ratio));
    if (this.canvas.width !== width || this.canvas.height !== height) {
      this.canvas.width = width;
      this.canvas.height = height;
    }
    paintBoard(this.#context, boardObjects(this.#board));
    this.#options.onPaint?.([...this.#board.editMarkers.values()]);
  }

  toBoardPoint(clientX: number, clientY: number): { x: number; y: number } {
    const bounds = this.canvas.getBoundingClientRect();
    return {
      x: ((clientX - bounds.left) / bounds.width) * BOARD_WIDTH,
      y: ((clientY - bounds.top) / bounds.height) * BOARD_HEIGHT,
    };
  }

  dispose(): void {
    if (this.#paintFrame) cancelAnimationFrame(this.#paintFrame);
    this.#resize.disconnect();
  }
}
